import { Button, Card, Form, Input, Modal, Select, Space, Table, Tag, notification, Flex } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import SparkMD5 from 'spark-md5';
import { useEffect, useRef, useState } from 'react';
import { Mpq } from '../api/go';

type CreateForm = {
  key: string;
  status: number;
};

type MpqRecord = {
  id: number;
  key: string;
  version: number;
  date: string;
  status: number;
  object_key: string;
  md5: string;
  data: string;
};

type MpqListResp = {
  key_list: string[];
  records: MpqRecord[];
};

function getErrorMessage(err: any) {
  if (err?.status) {
    return `请求失败: ${err.status}`;
  }
  if (err?.message) {
    return err.message;
  }
  return '请求失败';
}

export default function MpqManager() {
  const [listLoading, setListLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [uploadingId, setUploadingId] = useState<number>(0);
  const [records, setRecords] = useState<MpqRecord[]>([]);
  const [keyList, setKeyList] = useState<string[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [uploadTarget, setUploadTarget] = useState<MpqRecord | null>(null);
  const [createForm] = Form.useForm<CreateForm>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function loadList() {
    setListLoading(true);
    try {
      const data = (await Mpq.versionList()) as MpqListResp;
      setRecords(data?.records || []);
      setKeyList(data?.key_list || []);
    } catch (err) {
      notification.error({ message: getErrorMessage(err) });
    } finally {
      setListLoading(false);
    }
  }

  useEffect(() => {
    loadList();
  }, []);

  async function createVersion(values: CreateForm) {
    setCreateLoading(true);
    try {
      await Mpq.versionCreate(values);
      notification.success({ message: '创建成功' });
      setCreateOpen(false);
      createForm.resetFields();
      await loadList();
    } catch (err) {
      notification.error({ message: getErrorMessage(err) });
    } finally {
      setCreateLoading(false);
    }
  }

  function pickFile(row: MpqRecord) {
    setUploadTarget(row);
    fileInputRef.current?.click();
  }

  async function uploadFile(file: File, row: MpqRecord) {
    setUploadingId(row.id);
    try {
      const buf = await file.arrayBuffer();
      const md5 = SparkMD5.ArrayBuffer.hash(buf).toLowerCase();

      const token = await Mpq.ossToken({ key: row.key, version: row.version });
      const formData = new FormData();
      formData.append('key', token.object_key);
      formData.append('policy', token.policy);

      formData.append('x-oss-signature', token.signature);
      formData.append('x-oss-signature-version', token.x_oss_signature_version);
      formData.append('x-oss-credential', token.x_oss_credential);
      formData.append('x-oss-date', token.x_oss_date);
      formData.append('x-oss-security-token', token.security_token);

      formData.append('success_action_status', '200');
      formData.append('file', file);

      const uploadResp = await fetch(token.host, {
        method: 'POST',
        body: formData,
        mode: 'cors',
        credentials: 'omit',
      });

      if (!uploadResp.ok) {
        throw new Error(`oss upload failed: ${uploadResp.status}`);
      }

      await Mpq.uploadConfirm({ id: row.id, md5 });
      notification.success({ message: `上传并校验成功（md5: ${md5}）` });
      await loadList();
    } catch (err) {
      notification.error({ message: getErrorMessage(err) });
    } finally {
      setUploadingId(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setUploadTarget(null);
    }
  }

  const columns: ColumnsType<MpqRecord> = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 72,
    },
    {
      title: 'Key',
      dataIndex: 'key',
      width: 180,
    },
    {
      title: 'Version',
      dataIndex: 'version',
      width: 100,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 180,
      render: (status: number) => {
        const textMap: Record<number, string> = {
          0: '0 - 待上传文件',
          1: '1 - 已上传文件',
          100: '100 - 正式启用',
          101: '101 - Beta 启用',
        };
        const colorMap: Record<number, string> = {
          0: 'default',
          1: 'processing',
          100: 'success',
          101: 'purple',
        };
        return <Tag color={colorMap[status] || 'default'}>{textMap[status] || String(status)}</Tag>;
      },
    },
    {
      title: 'Object Key',
      dataIndex: 'object_key',
    },
    {
      title: 'MD5',
      dataIndex: 'md5',
      width: 260,
    },
    {
      title: 'Date',
      dataIndex: 'date',
      width: 220,
    },
    {
      title: '操作',
      width: 160,
      fixed: 'right',
      render: (_, row) => {
        if (row.status === 0) {
          // 初始状态的版本可以上传文件
          return (
            <Button type="primary" loading={uploadingId === row.id} onClick={() => pickFile(row)}>
              上传 MPQ 文件
            </Button>
          );
        }

        if (row.status === 1) {
          return (
            <div>
              <Flex gap="small" wrap>
                <Button
                  color="danger"
                  variant="solid"
                  onClick={async () => {
                    return Mpq.versionStatus({ id: row.id, status: 100 }).then(() => {
                      notification.success({ message: '设置成功' });
                      loadList();
                    });
                  }}
                >
                  设置为正式启用
                </Button>
                <Button
                  color="cyan"
                  variant="solid"
                  onClick={async () => {
                    return Mpq.versionStatus({ id: row.id, status: 101 }).then(() => {
                      notification.success({ message: '设置成功' });
                      loadList();
                    });
                  }}
                >
                  设置为 Beta 启用
                </Button>
              </Flex>
            </div>
          );
        }

        if (row.status === 101) {
          return (
            <div>
              <Flex gap="small" wrap>
                <Button
                  color="danger"
                  variant="solid"
                  onClick={async () => {
                    return Mpq.versionStatus({ id: row.id, status: 100 }).then(() => {
                      notification.success({ message: '设置成功' });
                      loadList();
                    });
                  }}
                >
                  设置为正式启用
                </Button>
              </Flex>
            </div>
          );
        }

        return '';
      },
    },
  ];

  return (
    <div>
      <Card
        styles={{ header: { backgroundColor: '#efefef' } }}
        title="MPQ 版本管理"
        extra={
          <Space>
            <Button onClick={loadList} loading={listLoading}>
              刷新
            </Button>
            <Button type="primary" onClick={() => setCreateOpen(true)}>
              创建新版本
            </Button>
          </Space>
        }
      >
        <Table<MpqRecord>
          rowKey="id"
          loading={listLoading}
          columns={columns}
          dataSource={records}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 1200 }}
        />
      </Card>

      <input
        ref={fileInputRef}
        type="file"
        accept=".mpq"
        style={{ display: 'none' }}
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (!file || !uploadTarget) {
            return;
          }
          await uploadFile(file, uploadTarget);
        }}
      />

      <Modal
        title="创建新版本"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => createForm.submit()}
        confirmLoading={createLoading}
        destroyOnClose
      >
        <Form<CreateForm> form={createForm} layout="vertical" initialValues={{ status: 0 }} onFinish={createVersion} preserve={false}>
          <Form.Item<CreateForm> label="Key" name="key" rules={[{ required: true, message: '请选择 key' }]}>
            <Select showSearch placeholder="选择 key" options={keyList.map((item) => ({ label: item, value: item }))} />
          </Form.Item>
          <Form.Item<CreateForm> label="初始状态" name="status" rules={[{ required: true, message: '请选择状态' }]}>
            <Select
              options={[
                { value: 0, label: '0 - 待上传' },
                { value: 1, label: '1 - 已上传校验' },
                { value: 100, label: '100 - 正式启用' },
                { value: 101, label: '101 - Beta 启用' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
